import React from 'react';
import { mcpServerUrl } from 'services/authService';
import { DATA_ACCESS_TEXT_STYLE, DataAccessText } from './dataAccessTypography';
import { CopyField, GuideStep } from './mcpGuideShared';
import mcpClaude01Settings from '../../assets/img/data-access/mcp-claude-01-settings.png';
import mcpClaude02Connectors from '../../assets/img/data-access/mcp-claude-02-connectors.png';
import mcpClaude03AddCustom from '../../assets/img/data-access/mcp-claude-03-add-custom.png';
import mcpClaude04AddForm from '../../assets/img/data-access/mcp-claude-04-add-form.png';
import mcpClaude05Connect from '../../assets/img/data-access/mcp-claude-05-connect.png';
import mcpCodex05SignIn from '../../assets/img/data-access/mcp-codex-05-sign-in.png';
import mcpClaude06Connected from '../../assets/img/data-access/mcp-claude-06-connected.png';
import mcpClaude07Permissions from '../../assets/img/data-access/mcp-claude-07-permissions.png';
import mcpClaude08Chat from '../../assets/img/data-access/mcp-claude-08-chat.png';

const ClaudeMcpGuide: React.FC = () => (
  <div>
    <DataAccessText className="text-700 mb-4">
      Follow the steps below in Claude Desktop or the Claude web app. Both use the same Connectors
      flow. Claude signs you in with your Shiplab account, so no API token is required.
    </DataAccessText>

    <GuideStep
      step={1}
      title="Open Settings"
      imageSrc={mcpClaude01Settings}
      imageAlt="Claude File menu with Settings highlighted"
    >
      Open Claude Settings with <strong>[Ctrl+,]</strong>, or click the menu button (three parallel
      lines) in the top left, then choose <strong>File → Settings…</strong>.
    </GuideStep>

    <GuideStep
      step={2}
      title="Open Connectors"
      imageSrc={mcpClaude02Connectors}
      imageAlt="Claude settings with Connectors selected under Customize"
    >
      In the sidebar, open the <strong>Customize</strong> section and select{' '}
      <strong>Connectors</strong>.
    </GuideStep>

    <GuideStep
      step={3}
      title="Add a custom connector"
      imageSrc={mcpClaude03AddCustom}
      imageAlt="Add menu open with Add custom connector selected"
    >
      Click <strong>Add</strong> in the top right, then choose <strong>Add custom connector</strong>.
    </GuideStep>

    <GuideStep
      step={4}
      title="Enter the MCP server URL"
      imageSrc={mcpClaude04AddForm}
      imageAlt="Add custom connector form with Name Shiplab and MCP server URL filled in"
    >
      <p className="mb-3" style={DATA_ACCESS_TEXT_STYLE}>
        Paste the MCP server URL below into the URL field. Choose a name for the connector (for
        example <strong>Shiplab</strong>). Leave the advanced OAuth fields empty, then click{' '}
        <strong>Add</strong>.
      </p>
      <CopyField label="URL" value={mcpServerUrl} />
    </GuideStep>

    <GuideStep
      step={5}
      title="Connect the connector"
      imageSrc={mcpClaude05Connect}
      imageAlt="Shiplab connector detail page with Connect button"
    >
      Open the new connector and click <strong>Connect</strong>.
    </GuideStep>

    <GuideStep
      step={6}
      title="Sign in to Shiplab"
      imageSrc={mcpCodex05SignIn}
      imageAlt="Shiplab sign-in form asking for username and password"
    >
      A browser window opens asking you to sign in with your Shiplab username and password. Use the
      same connectors you use for the Shiplab web app.
    </GuideStep>

    <GuideStep
      step={7}
      title="Confirm you are connected"
      imageSrc={mcpClaude06Connected}
      imageAlt="Connected confirmation screen with Open desktop app button"
    >
      When you see the <strong>Connected</strong> screen, click <strong>Open desktop app</strong>{' '}
      (or close the tab if you are already in Claude). You are now linked to Shiplab.
    </GuideStep>

    <GuideStep
      step={8}
      title="Review tool permissions"
      imageSrc={mcpClaude07Permissions}
      imageAlt="Shiplab connector tool permissions list in Claude"
    >
      Back in Connectors, open your connector to review which tools Claude may use (connector groups,
      connectors, profile, and more). You can leave tools on <strong>Needs approval</strong> so
      Claude asks before each action.
    </GuideStep>

    <GuideStep
      step={9}
      title="Try it in a chat"
      imageSrc={mcpClaude08Chat}
      imageAlt="Claude chat showing Shiplab profile details after using the connector"
    >
      Start a conversation in Claude and ask about your Shiplab account, for example &quot;get my
      shiplab profile&quot;. Claude calls Shiplab on your behalf and returns live data without leaving
      the conversation.
    </GuideStep>
  </div>
);

export default ClaudeMcpGuide;
